const VIN_PATTERN = /^((?![IOQ])[A-Z0-9]){17}$/

function debounce(fun: (a?: any) => any, time: number, maxInvocations?: number) {
    const store: { handler: NodeJS.Timeout | undefined, invocationCounter: number } = {
        handler: undefined,
        invocationCounter: 0
    }

    return (a?: any) => {
        store.invocationCounter = store.invocationCounter + 1

        if (!maxInvocations || store.invocationCounter <= maxInvocations) {
            clearTimeout(store.handler)
            store.handler = setTimeout(() => {
                store.invocationCounter = 0
                fun(a)
            }, time)
        }
    }
}

function equals(o1: any, o2: any): boolean {
    const replacer = (_: any, value: any) =>
        value instanceof Object && !(value instanceof Array)
            ? Object.keys(value)
                .sort()
                .reduce((sorted: any, reducerKey: string) => {
                    sorted[reducerKey] = value[reducerKey]
                    return sorted
                }, {})
            : value

    const s1 = JSON.stringify(o1, replacer)
    const s2 = JSON.stringify(o2, replacer)
    return s1 === s2
}

const idStore = {id: 0}

function nextId() {
    idStore.id += 1
    return idStore.id
}

function dateFormat(date: string | undefined, withoutTime?: boolean): string {
    if (!date) {
        return ''
    }
    const d = new Date(date)
    if (!withoutTime) {
        return d.toLocaleString('de')
    }
    return d.toLocaleDateString('de')
}

function removeAllButFirstDot(str: string) {
    const firstDotIndex = str.indexOf('.');

    if (firstDotIndex === -1) return str; // Keine Punkte vorhanden

    const beforeFirstDot = str.substring(0, firstDotIndex + 1);
    const afterFirstDot = str.substring(firstDotIndex + 1);

    return beforeFirstDot + afterFirstDot.replace(/\./g, '');
}

function toNumberString(v: string) {
    const t = v.replace(/[,]+/g, '.')
    const withoutLetters = t.toUpperCase().replace(/[^\d.]+/g, "")
    return removeAllButFirstDot(withoutLetters)
}

export interface ContextVal {
    param: string
    value?: string[]
}

interface Context {
    val: ContextVal[]
    searchParams: URLSearchParams
    setSearchParams: (p: URLSearchParams) => any
}

const updateUrlParams: (c: Context) => any = debounce((c: Context) => {
    c.val.forEach(x => {
        c.searchParams.delete(x.param)
        if (x.value && x.value.length) {
            x.value.forEach(val => c.searchParams.append(x.param, val))
        }
    })

    c.searchParams.set('f', 'on')
    if(Boolean(c.searchParams.get('page'))) {
        c.searchParams.set('page', '1')
    }

    c.setSearchParams(c.searchParams)
}, 500)

interface FieldDateSwitchValue {
    absolute?: string
    relative?: string
}

function updateUrlOnDateSwitchChange(
    v: FieldDateSwitchValue,
    key: string,
    searchParams: URLSearchParams,
    setSearchParams: (p: URLSearchParams) => any
) {
    const {absolute, relative} = v
    const vals: ContextVal[] = []

    if (absolute) {
        vals.push({
            param: `${key}Relative`, value: []
        }, {
            param: `${key}Absolute`, value: absolute === '' ? [] : [absolute]
        })
    } else if (relative) {
        vals.push({
            param: `${key}Relative`, value: relative !== '' ? [relative] : []
        }, {
            param: `${key}Absolute`, value: []
        })
    } else {
        vals.push({
            param: `${key}Relative`, value: []
        }, {
            param: `${key}Absolute`, value: []
        })
    }
    updateUrlParams({val: vals, setSearchParams, searchParams})
}

function isValidVIN(val: string): boolean {
    return VIN_PATTERN.test(val)
}


export type DurationType = 'DAYS' | 'HOURS' | 'MINUTES'

export interface Duration {
    type: DurationType
    value: number
}

function durationToIso8601String(val: Duration): string {
    switch (val.type) {
        case "DAYS":
            return `P${val.value}D`
        case "HOURS":
            return `PT${val.value}H`
        case "MINUTES":
            return `PT${val.value}M`
    }
}

function parseIso8601StringToDuration(iso8601: string): Duration | undefined {
    const normalized = iso8601?.trim().toUpperCase()
    if (!normalized) {
        return undefined
    }

    const durationMatch = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/.exec(normalized)
    if (!durationMatch) {
        return undefined
    }

    const days = durationMatch[1] ? parseInt(durationMatch[1], 10) : 0
    const hours = durationMatch[2] ? parseInt(durationMatch[2], 10) : 0
    const minutes = durationMatch[3] ? parseInt(durationMatch[3], 10) : 0

    if (minutes > 0) {
        return {
            type: 'MINUTES',
            value: days * 24 * 60 + hours * 60 + minutes
        }
    }

    if (hours > 0) {
        return {
            type: 'HOURS',
            value: days * 24 + hours
        }
    }

    if (days > 0) {
        return {
            type: 'DAYS',
            value: days
        }
    }

    return undefined
}


type Range<N extends number, Result extends Array<number> = []> =
    Result['length'] extends N
        ? Result[number]
        : Range<N, [...Result, Result['length']]>;

export type HourType = Range<24>;
export type MinuteType = Range<60>;

export interface Time {
    hours: HourType
    minutes: MinuteType
}

function parseStringToTime(input: string | undefined): Time | undefined {
    if (!input) {
        return undefined
    }
    const parts = input.split(':')
    if (parts.length < 2) {
        return undefined
    }
    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    if (isNaN(hours) || isNaN(minutes) || minutes < 0 || hours < 0 || minutes > 59 || hours > 23) {
        return undefined
    }
    return {
        hours: hours as HourType,
        minutes: minutes as MinuteType
    }
}

export {
    VIN_PATTERN,
    isValidVIN,
    parseIso8601StringToDuration,
    durationToIso8601String,
    parseStringToTime,
    toNumberString,
    updateUrlParams,
    updateUrlOnDateSwitchChange,
    dateFormat,
    nextId,
    equals,
    debounce
}
